import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip,
} from 'recharts';
import { LayoutGrid, ArrowUpDown, Info, Radar as RadarIcon } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { usePost } from '@/lib/useData';
import { comparisonInfo } from '@/lib/periods';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { GridSkeleton, CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

// Metrics shown in the comparison matrix. `better` drives the heat direction.
const METRICS = [
  { key: 'bs_rue', label: 'BS signés', fmt: (v) => fmtInt(v), better: 'high' },
  { key: 'bs_reel', label: 'BS validés', fmt: (v) => fmtInt(v), better: 'high' },
  { key: 'taux_transfo', label: 'Taux transfo', fmt: (v) => fmtPct(v), better: 'high' },
  { key: 'don_moyen', label: 'Don moyen', fmt: (v) => fmtEur(v, 2), better: 'high' },
  { key: 'montant', label: 'Montant', fmt: (v) => fmtEur(v, 0), better: 'high' },
  { key: 'productivite', label: 'Prod. (BS/h)', fmt: (v) => fmtNum(v, 2), better: 'high' },
  { key: 'age_moyen', label: 'Âge moyen', fmt: (v) => `${fmtNum(v, 1)} ans`, better: 'neutral' },
];

// Radar axes (only "higher-is-better" metrics, normalised 0..100).
const RADAR_METRICS = [
  { key: 'bs_reel', label: 'BS validés' },
  { key: 'taux_transfo', label: 'Transfo' },
  { key: 'don_moyen', label: 'Don moyen' },
  { key: 'montant', label: 'Montant' },
  { key: 'productivite', label: 'Productivité' },
];

function heat(n, neutral) {
  if (neutral) return 'rgba(100,116,139,0.10)';
  const a = 0.05 + 0.34 * n;
  return `rgba(238,114,46,${a.toFixed(3)})`;
}

export default function Comparatif() {
  const { body, periodKey, period, comparePeriod, clearAll } = useFilters();
  const { data, isLoading, isError, error, refetch } = usePost('/admin/analytics/consolidated', body);
  const isForbidden = error?.response?.status === 403;
  const [sortKey, setSortKey] = useState('bs_reel');

  const ranking = data?.ranking || [];
  const info = useMemo(() => comparisonInfo(periodKey, period, comparePeriod), [periodKey, period, comparePeriod]);

  // Per-metric extents (for heat normalisation + leader detection).
  const extents = useMemo(() => {
    const e = {};
    METRICS.forEach((m) => {
      const vals = ranking.map((r) => r[m.key] || 0);
      e[m.key] = { min: Math.min(...vals, 0), max: Math.max(...vals, 0) };
    });
    return e;
  }, [ranking]);

  const rows = useMemo(() => {
    const sorted = [...ranking];
    sorted.sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    return sorted;
  }, [ranking, sortKey]);

  const radarData = useMemo(() => {
    const maxByKey = {};
    RADAR_METRICS.forEach((m) => { maxByKey[m.key] = Math.max(1, ...ranking.map((r) => r[m.key] || 0)); });
    return RADAR_METRICS.map((m) => {
      const row = { metric: m.label };
      ranking.forEach((r) => { row[r.code] = Math.round(((r[m.key] || 0) / maxByKey[m.key]) * 100); });
      return row;
    });
  }, [ranking]);

  const norm = (m, v) => {
    const { min, max } = extents[m.key] || { min: 0, max: 1 };
    if (max === min) return 0.5;
    return (v - min) / (max - min);
  };

  return (
    <div className="space-y-6" data-testid="comparatif-page">
      <PageHeader title="Comparatif associations" icon={LayoutGrid}
        subtitle="Comparez les associations sur tous les indicateurs de performance" />

      {isForbidden ? (
        <EmptyState title="Accès restreint" body="Cette vue est réservée aux administrateurs SHARITI." />
      ) : isError ? (
        <ErrorState
          title="Impossible de charger le comparatif"
          body="Le calcul consolidé a échoué ou expiré. Réessayez dans un instant."
          onRetry={refetch}
        />
      ) : isLoading ? (
        <>
          <CardSkeleton />
          <GridSkeleton count={2} />
        </>
      ) : !data || ranking.length === 0 ? (
        <EmptyState title="Aucune donnée" body="Aucune donnée consolidée sur la période sélectionnée." onClear={clearAll} />
      ) : (
        <>
          {/* Multi-indicator heatmap matrix */}
          <SectionCard
            title="Matrice de performance"
            subtitle="Plus la teinte est intense, meilleure est la valeur · le leader de chaque colonne est encadré"
            bodyClassName="pt-0"
            action={
              info.curRange && (
                <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
                  Période&nbsp;: <span className="tabular font-medium text-foreground/80">{info.curRange}</span>
                </span>
              )
            }
            testid="comparatif-matrix">
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 text-sm" data-testid="comparatif-matrix-table">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-card px-3 py-2.5 text-left font-medium text-muted-foreground">
                      Association
                    </th>
                    {METRICS.map((m) => (
                      <th key={m.key} className="px-2 py-2.5 text-right">
                        <button
                          onClick={() => setSortKey(m.key)}
                          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium transition-colors hover:text-foreground ${sortKey === m.key ? 'text-foreground' : 'text-muted-foreground'}`}
                          data-testid={`matrix-sort-${m.key}`}>
                          {m.label}
                          <ArrowUpDown className={`h-3 w-3 ${sortKey === m.key ? 'accent-text' : 'opacity-40'}`} />
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.code} data-testid={`matrix-row-${r.code}`} className="group">
                      <td className="sticky left-0 z-10 border-t bg-card px-3 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: r.primary_color || '#EE722E' }} />
                          <span className="font-medium">{r.short_name}</span>
                        </div>
                      </td>
                      {METRICS.map((m) => {
                        const v = r[m.key] || 0;
                        const neutral = m.better === 'neutral';
                        const isLeader = !neutral && extents[m.key].max > 0 && v === extents[m.key].max;
                        return (
                          <td key={m.key} className="border-t px-1.5 py-1.5 text-right">
                            <span
                              className={`inline-block min-w-[62px] rounded-lg px-2 py-1 tabular ${isLeader ? 'font-semibold text-foreground ring-1 ring-[var(--tenant-accent-500)]' : 'text-foreground/80'}`}
                              style={{ background: heat(norm(m, v), neutral) }}>
                              {m.fmt(v)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5" />
              Cliquez sur un indicateur pour trier · « Âge moyen » est neutre (ni bon ni mauvais).
            </div>
          </SectionCard>

          {/* Radar + per-metric podium */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <SectionCard
              title="Profil comparé (radar)"
              subtitle="Indicateurs normalisés à 100 (meilleure association par axe)"
              className="xl:col-span-2"
              action={<RadarIcon className="h-5 w-5 text-muted-foreground" />}
              testid="comparatif-radar">
              <div style={{ height: 360 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <RadarChart data={radarData} outerRadius="72%">
                    <PolarGrid stroke="rgba(11,19,32,0.10)" />
                    <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12, fill: '#5B6472' }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#98A2B3' }} />
                    {ranking.map((r) => (
                      <Radar key={r.code} name={r.short_name} dataKey={r.code}
                        stroke={r.primary_color || '#EE722E'} fill={r.primary_color || '#EE722E'}
                        fillOpacity={0.08} strokeWidth={2} />
                    ))}
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Tooltip formatter={(val) => `${val}/100`} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>

            <SectionCard title="Leaders par indicateur" subtitle="Qui est en tête sur chaque métrique"
              testid="comparatif-leaders">
              <div className="space-y-2.5">
                {METRICS.filter((m) => m.better !== 'neutral').map((m) => {
                  const leader = [...ranking].sort((a, b) => (b[m.key] || 0) - (a[m.key] || 0))[0];
                  if (!leader) return null;
                  return (
                    <div key={m.key} className="flex items-center justify-between rounded-xl border bg-card px-3 py-2" data-testid={`leader-${m.key}`}>
                      <span className="text-xs text-muted-foreground">{m.label}</span>
                      <span className="flex items-center gap-2 text-sm">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: leader.primary_color || '#EE722E' }} />
                        <span className="font-medium">{leader.short_name}</span>
                        <span className="tabular text-muted-foreground">{m.fmt(leader[m.key] || 0)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
